<template>
  <div class="container relative">
    <form
      class="w-[calc(100%-2rem)] absolute md:px-[15px] md:left-1/2 md:transform md:-translate-x-1/2 md:w-[450px] lg:w-[550px] -top-[75px] md:-top-[85px]"
      @submit.prevent="$refs.vipsFilter.submitFilter"
    >
      <div class="relative">
        <img
          src="/frontend/assets/input-zoom.svg"
          class="absolute left-[16px] top-[15px] lg:top-[18px] lg:left-[20px]"
        >

        <input
          v-model="textSearch"
          type="search"
          class="w-full py-[13px] text-lg rounded-xl shadow-input placeholder-gray-250 border-red border-2 focus:ring-red-900 focus:ring-offset-red-900 focus:border-red-900 pl-12 lg:py-4"
          :placeholder="__('vips.search.placeholder')"
          maxlength="100"
        >
      </div>
    </form>

    <div class="flex flex-wrap md:items-center lg:mt-4">
      <div class="hidden md:block text-center w-full md:order-2 md:w-1/3 lg:w-1/6">
        <span class="block text-center md:text-right">
          {{ $t('components.vips-filter.found', totalVips) }}
        </span>
      </div>

      <div class="w-full flex flex-wrap md:order-1 md:w-2/3 lg:w-5/6 relative z-10 items-center md:space-x-2 lg:space-x-3">
        <div class="inline-flex flex-row w-full md:w-auto space-x-2 md:space-x-3 mb-2 md:mb-0">
          <template
            v-for="category in popularCategories"
            :key="`popular-category-${category.slug}`"
          >
            <Link
              type="button"
              class="w-full border-2 border-black block text-center text-sm font-bold py-3 rounded-lg md:w-auto md:px-8 md:mt-0 hover:bg-gray-100 bg-white"
              :href="localeRoute('frontend.categories.slug', [category.slug])"
            >
              {{ category.name }}
            </Link>
          </template>
        </div>

        <button
          type="button"
          class="w-full border-2 border-black block text-center text-sm font-bold py-3 rounded-lg md:w-auto md:px-8 md:mt-0 hover:bg-gray-100 bg-white"
          @click="open = true"
        >
          {{ __('components.vips-filter.title') }}
          <span
            v-show="selectedFilters.length > 0"
            class="rounded-full bg-red text-white text-[10px] inline-block w-[15px] h-[15px] text-center leading-[1.6] ml-1"
          >
            {{ selectedFilters.length }}
          </span>
        </button>

        <button
          v-for="(selectedFilter, i) in selectedFilters"
          :key="`selected-filter-${i}`"
          class="text-xs border border-gray-300 text-gray-300 py-2 px-3 rounded-md hidden md:block"
          @click="selectedFilter.remove"
        >
          {{ selectedFilter.title }}

          <img
            src="/frontend/assets/cancel-black.svg"
            class="inline ml-1"
          >
        </button>
      </div>
    </div>
  </div>

  <TransitionRoot
    as="template"
    :show="open"
  >
    <DialogComponent
      as="div"
      class="relative z-50"
      @close="close"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed z-50 inset-0 overflow-y-auto">
        <div class="flex items-end sm:items-center justify-center min-h-full p-4 sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="bg-white fixed bottom-0 left-0 right-0 rounded-t-3xl p-6 md:left-[5%] md:right-[5%] lg:right-[15%] lg:left-[15%] md:bottom-auto md:top-1/2 md:transform md:-translate-y-1/2 md:rounded-3xl lg:px-12 lg:py-6 max-h-full overflow-y-scroll">
              <div class="flex flex-wrap items-center">
                <div class="w-1/6 lg:order-3 lg:flex lg:justify-end">
                  <button
                    type="button"
                    @click="close"
                  >
                    <img src="/frontend/assets/close-filter.svg">
                  </button>
                </div>

                <div class="w-4/6 text-center text-2xl font-bold lg:order-2 lg:text-4xl">
                  {{ __('components.vips-filter.title') }}
                </div>

                <div class="w-1/6 text-right lg:order-1">
                  <button
                    type="button"
                    class="lg:hidden"
                    @click="resetFilter"
                  >
                    {{ __('components.vips-filter.reset') }}
                  </button>
                </div>
              </div>

              <div class="flex flex-wrap lg:mt-2 max-h-full overflow-auto">
                <div class="w-full md:w-1/2 md:pr-3">
                  <div class="mt-6">
                    <span class="text-xl font-bold mb-2 block">
                      {{ __('components.vips-filter.sort') }}
                    </span>

                    <div class="text-lg space-y-2.5 all-vips-filter-buttons">
                      <button
                        v-for="(name, key) in sortingCategories"
                        :key="`sorting-category-${key}`"
                        type="button"
                        class="block hover:underline"
                        :class="{ 'active underline': form.selectedSortingCategory === key }"
                        @click="form.selectedSortingCategory = form.selectedSortingCategory !== key ? key : null;"
                      >
                        {{ name }}
                      </button>
                    </div>
                  </div>
                </div>

                <div class="w-full my-6 md:w-1/2 md:pl-3">
                  <!-- eslint-disable vue/no-v-html -->
                  <span
                    class="text-xl font-bold mb-2 block"
                    v-html="__('components.vips-filter.categories')"
                  />
                  <!-- eslint-enable vue/no-v-html -->

                  <div class="grid grid-cols-2 gap-2 all-vips-filter-cat">
                    <button
                      v-for="category in categories.slice(0, 8)"
                      :key="`filter-category-${category.slug}`"
                      class="text-center w-full rounded-xl text-lg py-4"
                      :class="form.selectedCategory === category.slug ? 'bg-red text-white' : 'bg-white text-black border border-black'"
                      @click="addCategory(category.slug)"
                    >
                      {{ category.name }}

                      <img
                        v-show="form.selectedCategory === category.slug"
                        src="/frontend/assets/check-white.svg"
                        class="inline ml-1"
                      >
                    </button>
                  </div>

                  <div class="relative mt-2">
                    <Multiselect
                      :options="categories.map((category) => ({ value: category.slug, label: category.name }))"
                      :searchable="true"
                      :caret="true"
                      :can-clear="true"
                      placeholder="Zoek categorieën..."
                      mode="single"
                      :classes="{
                        container: 'relative mx-auto w-full flex items-center justify-end box-border cursor-pointer border border-black rounded-md bg-white text-base leading-snug outline-none shadow-sm',
                        containerDisabled: 'cursor-default bg-gray-100',
                        containerOpen: 'rounded-b-none ring-1 ring-red-500 border-red-500',
                        containerOpenTop: 'rounded-t-none',
                        containerActive: 'ring-1 ring-red-500 border-red-500',
                        singleLabel: 'flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 text-sm',
                        multipleLabel: 'flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5',
                        search: 'w-full h-full absolute inset-0 outline-none focus:ring-0 appearance-none box-border border-0 text-base font-sans bg-white rounded pl-3.5',
                        tags: 'grow shrink flex flex-wrap mt-1 pl-2',
                        tag: 'bg-red-500 text-white text-sm font-semibold py-0.5 pl-2 rounded mr-1 mb-1 flex items-center',
                        tagDisabled: 'pr-2 !bg-gray-400 text-white',
                        tagRemove: 'flex items-center justify-center p-1 mx-0.5 rounded-sm hover:bg-black hover:bg-opacity-10 group',
                        tagRemoveIcon: 'bg-multiselect-remove bg-center bg-no-repeat opacity-30 inline-block w-3 h-3 group-hover:opacity-60',
                        tagsSearch: 'h-full border-0 outline-none focus:ring-0 appearance-none p-0 text-base font-sans mx-1 mb-1 box-border grow shrink',
                        placeholder: 'flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent pl-3.5 text-gray-500 text-sm',
                        caret: 'bg-multiselect-caret bg-center bg-no-repeat w-6 h-6 py-px box-content mr-3.5 relative z-10 opacity-40 shrink-0 grow-0 transition-transform transform',
                        caretOpen: 'rotate-180',
                        clear: 'pr-3.5 relative z-10 opacity-40 transition duration-300 shrink-0 grow-0 flex hover:opacity-80 top-3 left-[110px]',
                        clearIcon: 'bg-multiselect-remove bg-center bg-no-repeat w-2.5 h-4 py-px box-content inline-block',
                        spinner: 'bg-multiselect-spinner bg-center bg-no-repeat w-4 h-4 z-10 mr-3.5 animate-spin shrink-0 grow-0',
                        dropdown: 'absolute -left-px -right-px bottom-0 transform translate-y-full border border-gray-300 -mt-px overflow-y-scroll z-50 bg-white flex flex-col rounded-b max-h-[160px]',
                        dropdownTop: '-translate-y-full top-px bottom-auto flex-col-reverse rounded-b-none rounded-t',
                        dropdownHidden: 'hidden',
                        options: 'flex flex-col p-0 m-0 list-none',
                        optionsTop: 'flex-col-reverse',
                        option: 'flex items-center justify-start box-border text-left cursor-pointer text-base leading-snug py-2 px-3',
                        optionPointed: 'text-gray-800 bg-gray-100',
                        optionSelected: 'text-white bg-red-500',
                        optionDisabled: 'text-gray-300 cursor-not-allowed',
                        optionSelectedPointed: 'text-white bg-red-500 opacity-90',
                        optionSelectedDisabled: 'text-red-100 bg-red-500 bg-opacity-50 cursor-not-allowed',
                        noOptions: 'py-2 px-3 text-gray-600 bg-white',
                        noResults: 'py-2 px-3 text-gray-600 bg-white',
                        fakeInput: 'bg-transparent absolute left-0 right-0 -bottom-px w-full h-px border-0 p-0 appearance-none outline-none text-transparent',
                        spacer: 'h-9 box-content',
                      }"
                      @change="addCategory($event)"
                    />
                  </div>
                </div>

                <div class="w-full md:mt-8 lg:text-center">
                  <button
                    type="button"
                    class="button-orange shadow-button-black block w-full lg:inline-block lg:w-auto lg:px-24"
                    @click="submitFilter(true)"
                  >
                    <span v-if="totalVips > 0">
                      {{ $t('components.vips-filter.found-dialog', totalVips) }}
                    </span>
                    <span v-else>
                      {{ $t('components.vips-filter.nothing-found', totalVips) }}
                    </span>
                  </button>

                  <button
                    type="button"
                    class="hidden lg:block lg:mt-4 lg:mx-auto hover:underline"
                    @click="resetFilter"
                  >
                    {{ __('components.vips-filter.reset-dialog') }}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </DialogComponent>
  </TransitionRoot>
</template>

<script>
import { Dialog as DialogComponent, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {cloneDeep} from 'lodash';
import {defineAsyncComponent, ref} from 'vue';

export default {
  components: {
    DialogComponent,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
    Multiselect: defineAsyncComponent(() => import('@vueform/multiselect')),
  },

  props: {
    search: {
      type: String,
      required: true,
    },

    totalVips: {
      type: Number,
      required: true,
    },

    highestVipPrice: {
      type: Number,
      required: true,
    },

    occasions: {
      type: Array,
      required: true,
    },

    popularCategories: {
      type: Array,
      required: true,
    },

    categories: {
      type: Array,
      required: true,
    },

    initialSearch: {
      type: String,
      required: false,
      default: '',
    },

    initialFilter: {
      type: Object,
      required: false,
      default: () => {},
    },
  },

  emits: ['update:open', 'update:search', 'filter:reset', 'filter:recalculate', 'filter:submit'],

  setup(props) {
    let form = ref({
      selectedSortingCategory: null,
      selectedOccasion: null,
      selectedCategory: null,
      priceSort: [0, props.highestVipPrice],
    });

    if (Object.hasOwnProperty.call(props.initialFilter, 'sort')) {
      form.value.selectedSortingCategory = ref(props.initialFilter.sort).value;
    }
    if (Object.hasOwnProperty.call(props.initialFilter, 'occasion')) {
      form.value.selectedOccasion = ref(props.initialFilter.occasion).value;
    }
    if (Object.hasOwnProperty.call(props.initialFilter, 'category')) {
      form.value.selectedCategory = ref(props.initialFilter.category).value;
    }
    if (Object.hasOwnProperty.call(props.initialFilter, 'price_min')) {
      form.value.priceSort[0] = ref(props.initialFilter.price_min).value;
    }
    if (Object.hasOwnProperty.call(props.initialFilter, 'price_max') && props.initialFilter.price_max < props.highestVipPrice) {
      form.value.priceSort[1] = ref(props.initialFilter.price_max).value;
    }

    return {form};
  },

  data() {
    return {
      open: false,

      sortingCategories: {
        newest: 'Nieuwste VIPS',
        price: 'Prijs oplopend',
        within_2_days: 'Binnen 48 uur geleverd',
      },

      textSearch: this.initialSearch || '',
      filter: cloneDeep(this.initialFilter),
    };
  },

  computed: {
    selectedFilters() {
      const filters = [];

      if (this.textSearch && this.textSearch.length > 0) {
        filters.push({
          title: `Zoeken: "${this.textSearch}"`,
          remove: () => {
            this.textSearch = '';
            this.$nextTick(() => this.submitFilter());
          },
        });
      }
      if ('occasion' in this.filter) {
        filters.push({
          title: this.occasions.find((occasion) => this.filter.occasion === occasion.slug).short_name,
          remove: () => {
            this.form.selectedOccasion = null;
            this.submitFilter();
          },
        });
      }
      if ('category' in this.filter) {
        filters.push({
          title: this.categories.find((category) => this.filter.category === category.slug).name,
          remove: () => {
            this.form.selectedCategory = null;
            this.submitFilter();
          },
        });
      }
      if ('sort' in this.filter) {
        filters.push({
          title: this.sortingCategories[this.filter.sort],
          remove: () => {
            this.form.selectedSortingCategory = null;
            this.submitFilter();
          },
        });
      }
      if ('price_min' in this.filter) {
        filters.push({
          title: `Vanaf €${this.filter.price_min}`,
          remove: () => {
            this.form.priceSort[0] = 0;
            this.submitFilter();
          },
        });
      }
      if ('price_max' in this.filter) {
        filters.push({
          title: `Tot €${this.filter.price_max}`,
          remove: () => {
            this.form.priceSort[1] = this.highestVipPrice;
            this.submitFilter();
          },
        });
      }

      return filters;
    },
  },

  watch: {
    textSearch() {
      this.$nextTick(() => {
        this.submitFilter();
      });
    },

    form: {
      deep: true,
      handler: function() { this.recalculateFilter(); },
    },
  },

  methods: {
    close() {
      this.open = false;
    },

    addCategory(categorySlug) {
      this.form.selectedCategory = this.form.selectedCategory !== categorySlug ? categorySlug : null;
    },

    buildFilter() {
      const filter = {};

      if (this.textSearch.length > 0) {
        filter.search = this.textSearch;
      }
      if (this.form.selectedSortingCategory !== null) {
        filter.sort = this.form.selectedSortingCategory;
      }
      if (this.form.selectedOccasion !== null) {
        filter.occasion = this.form.selectedOccasion;
      }
      if (this.form.selectedCategory !== null) {
        filter.category = this.form.selectedCategory;
      }
      if (this.form.priceSort[0] > 0) {
        filter.price_min = this.form.priceSort[0];
      }
      if (this.form.priceSort[1] < this.highestVipPrice) {
        filter.price_max = this.form.priceSort[1];
      }

      this.filter = filter;
    },

    resetFilter() {
      this.form.selectedOccasion = null;
      this.form.selectedCategory = null;
      this.form.selectedSortingCategory = null;
      this.filter = {};

      this.close();
      this.$emit('filter:reset');
    },

    recalculateFilter() {
      this.buildFilter();

      this.$emit('filter:recalculate', this.filter);
    },

    submitFilter(close = false, initial = false) {
      this.buildFilter();

      if (close) {
        this.close();
      }

      this.$emit('filter:submit', this.filter, initial);
    },
  },
};
</script>
